import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Coins } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePlatformAuth } from "@/contexts/PlatformAuthContext";
import axios from "axios";
import { trackClick } from "@/lib/track";
import { ThemeToggle } from "@/components/common/ThemeToggle";
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher";
import { SectionGlow } from "@/components/common/SectionGlow";
import { SButton } from "@/components/common/SButton";
import { LogoutControl } from "@/components/platform/LogoutControl";
import { CopyWithCoinPrice, COIN_PRICE_SLOT } from "@/components/common/CoinPrice";
import { BuyCoinsPanel } from "@/components/wallet/BuyCoinsPanel";
import { useJoinPrice } from "@/hooks/useJoinPrice";
import { BrandMark } from "@/components/common/BrandMark";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Navigation() {
  const { t } = useLanguage();
  const { platformUser, loading: authLoading } = usePlatformAuth();
  // One rule only: logged out shows Join, logged in shows the Account menu.
  // While auth resolves, show the logged-out state so the menu never flashes.
  const loggedIn = !authLoading && !!platformUser;
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Buy Coins is for everyone. Logged in opens the panel; logged out routes to the
  // existing login page carrying ?next so they land on Billing (where the wallet +
  // Buy Coins panel live) instead of the home page. The magic-link sign-in path is
  // reused untouched.
  const buyCoinsAction = () => {
    if (loggedIn) setBuyOpen(true);
    else navigate("/login?next=/account/billing");
  };

  // Coin balance for the Buy Coins control (logged-in only). Refreshed after a top-up.
  useEffect(() => {
    if (!loggedIn) { setWallet(null); return; }
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const token = localStorage.getItem("samtwo_platform_token") || localStorage.getItem("samtwo_token");
    axios.get(`${API}/coins/wallet`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => setWallet(r.data)).catch(() => {});
  }, [loggedIn]);

  // Join CTA price: shared live source (Early Bird > promo > regular), never hardcoded.
  const { joinCoins, joinDollars } = useJoinPrice();
  const joinTpl = t("nav.join", { price: COIN_PRICE_SLOT, coins: joinCoins, dollars: joinDollars });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/subscribers", label: "Subscribers" },
    { to: "/shop", label: t("nav.shop") },
    { to: "/licensing", label: t("nav.licensing") },
  ];

  const linkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors ${
      isActive ? "text-[color:var(--accent-primary)]" : "text-[color:var(--text-primary)] hover:text-[color:var(--accent-primary)]"
    }`;

  return (
    <>
    <motion.header
      data-testid="main-nav"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={
        scrolled
          ? { background: "var(--nav-glass-bg)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderBottom: "1px solid var(--nav-glass-border)", boxShadow: "var(--elev-1)" }
          : { background: "transparent" }
      }
    >
      <nav className="chrome-wide max-w-7xl mx-auto px-5 sm:px-8 h-16 grid grid-cols-[1fr_auto_1fr] items-center">
        {/* Existing header identity slot; no extra row. */}
        <Link to="/" className="flex items-center justify-self-start" aria-label="SAMTWO home" data-testid="nav-logo">
          <BrandMark mark="clef" testId="nav-clef" alt="" />
        </Link>

        {/* Center — links (desktop) */}
        <div className="hidden md:flex items-center gap-8 justify-self-center">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-link-${l.label.toLowerCase()}`} onClick={() => trackClick(`nav_${(l.to.replace("/", "") || "home")}`)}>
              {l.label}
            </NavLink>
          ))}
        </div>

        {/* Right — controls. Desktop: Language, Join, Theme. Mobile: Language + hamburger.
            col-start-3 pins this to the right track even when the center links are
            display:none on mobile (otherwise it auto-places into the middle track). */}
        <div className="col-start-3 flex items-center gap-1 sm:gap-2 justify-self-end">
          <LanguageSwitcher />
          {/* Buy Coins (desktop only; mobile uses the drawer so the 320px header stays
              Language + hamburger). Logged in: a balance pill. Logged out: a compact
              icon-only button so the Join CTA stays primary and the header never crowds. */}
          {loggedIn ? (
            <button onClick={buyCoinsAction} data-testid="nav-buycoins" title={t("nav.buyCoins")} aria-label={t("nav.buyCoins")}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold transition-transform hover:-translate-y-0.5"
              style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}>
              <Coins size={15} style={{ color: "var(--accent-primary)" }} />
              {wallet != null && <span data-testid="nav-coin-balance">{wallet.balance_coins}</span>}
            </button>
          ) : (
            <button onClick={buyCoinsAction} data-testid="nav-buycoins-guest" title={t("nav.buyCoins")} aria-label={t("nav.buyCoins")}
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-transform hover:-translate-y-0.5"
              style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}>
              <Coins size={15} style={{ color: "var(--accent-primary)" }} /> {t("nav.buyCoins")}
            </button>
          )}
          {loggedIn ? (
            <div className="hidden sm:block" data-testid="nav-account">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SButton className="!py-2 !px-5 text-sm" data-testid="nav-account-trigger">{t("nav.account")}</SButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[12rem]" data-testid="nav-account-menu">
                  <DropdownMenuItem asChild data-testid="nav-account-explore">
                    <Link to="/u/samtwo">{t("nav.explore")}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="nav-account-purchases">
                    <Link to="/account/purchases">{t("purchases.headline")}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="nav-account-billing">
                    <Link to="/account/billing">{t("billing.navLabel")}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="nav-account-buycoins" onClick={() => setBuyOpen(true)}>
                    {t("nav.buyCoins")}{wallet != null ? ` (${wallet.balance_coins})` : ""}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1.5">
                    <LogoutControl testid="nav-account-logout" />
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <Link to="/pricing" className="hidden sm:block" data-testid="nav-join">
              <SButton className="!py-2 !px-5 text-sm"><span className="inline-flex items-baseline gap-1 whitespace-nowrap">{joinCoins != null && <CopyWithCoinPrice template={joinTpl} coins={joinCoins} size="sm" />}</span></SButton>
            </Link>
          )}
          <div className="hidden md:block"><ThemeToggle /></div>
          <button className="md:hidden p-2" aria-label="Menu" data-testid="nav-menu-toggle" onClick={() => setMenuOpen((o) => !o)} style={{ color: "var(--text-primary)" }}>
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>
    </motion.header>

    {/* Mobile menu — portaled to <body> so it is never inside the scrolled
        header's backdrop-filter (which becomes a containing block for fixed
        descendants and otherwise breaks the panel when scrolled down). */}
    {createPortal(
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden fixed inset-0 z-[60]"
              style={{ background: "rgba(10,15,26,0.4)", backdropFilter: "blur(2px)" }}
              onClick={() => setMenuOpen(false)}
              data-testid="nav-mobile-backdrop"
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 380, damping: 38 }}
              className="md:hidden fixed top-0 right-0 bottom-0 z-[61] w-[80vw] max-w-xs flex flex-col overflow-hidden"
              style={{ background: "var(--bg-surface)", borderLeft: "1px solid var(--border-color)", boxShadow: "var(--elev-3)" }}
              data-testid="nav-mobile-menu"
            >
              <SectionGlow position="70% 10%" strength={12} />
              <div className="relative z-10 flex items-center justify-between h-16 pl-6 pr-5" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                <BrandMark mark="clef" testId="nav-mobile-clef" />
                <button className="p-2" aria-label="Close menu" data-testid="nav-mobile-close" onClick={() => setMenuOpen(false)} style={{ color: "var(--text-primary)" }}>
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="relative z-10 flex-1 px-6 py-6 flex flex-col gap-5 overflow-y-auto">
                {links.map((l) => (
                  <NavLink key={l.to} to={l.to} className={({ isActive }) =>
                    `text-lg font-medium transition-colors ${isActive ? "text-[color:var(--accent-primary)]" : "text-[color:var(--text-primary)] hover:text-[color:var(--accent-primary)]"}`
                  } data-testid={`nav-mobile-${l.label.toLowerCase()}`} onClick={() => trackClick(`nav_${(l.to.replace("/", "") || "home")}`)}>
                    {l.label}
                  </NavLink>
                ))}
                {loggedIn ? (
                  <div className="mt-2 flex flex-col gap-5" data-testid="nav-mobile-account">
                    <Link to="/u/samtwo" data-testid="nav-mobile-explore" className="text-lg font-medium" style={{ color: "var(--text-primary)" }}>{t("nav.explore")}</Link>
                    <Link to="/account/purchases" data-testid="nav-mobile-purchases" className="text-lg font-medium" style={{ color: "var(--text-primary)" }}>{t("purchases.headline")}</Link>
                    <Link to="/account/billing" data-testid="nav-mobile-billing" className="text-lg font-medium" style={{ color: "var(--text-primary)" }}>{t("billing.navLabel")}</Link>
                    <button onClick={() => { setMenuOpen(false); setBuyOpen(true); }} data-testid="nav-mobile-buycoins"
                      className="text-lg font-medium text-left inline-flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
                      <Coins size={18} style={{ color: "var(--accent-primary)" }} /> {t("nav.buyCoins")}{wallet != null ? ` (${wallet.balance_coins})` : ""}
                    </button>
                    <div className="pt-2"><ThemeToggle className="!px-0" /></div>
                    <div><LogoutControl testid="nav-mobile-logout" /></div>
                  </div>
                ) : (
                  <>
                    <button onClick={() => { setMenuOpen(false); buyCoinsAction(); }} data-testid="nav-mobile-buycoins-guest"
                      className="text-lg font-medium text-left inline-flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
                      <Coins size={18} style={{ color: "var(--accent-primary)" }} /> {t("nav.buyCoins")}
                    </button>
                    <div className="pt-2"><ThemeToggle className="!px-0" /></div>
                    <Link to="/pricing" data-testid="nav-mobile-join" className="mt-2">
                      <SButton className="w-full !py-2.5"><span className="inline-flex items-baseline gap-1 whitespace-nowrap">{joinCoins != null && <CopyWithCoinPrice template={joinTpl} coins={joinCoins} size="sm" />}</span></SButton>
                    </Link>
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    )}
    <BuyCoinsPanel open={buyOpen} onOpenChange={setBuyOpen} onCredited={(w) => setWallet(w)} />
    </>
  );
}
